import type { LucideIcon } from 'lucide-react';
import {
  Ship,
  SlidersHorizontal,
  DollarSign,
  FileText,
  ClipboardCheck,
} from 'lucide-react';
import type { InventoryEngine, InventoryEquipment } from '@/modules/inventory/types/boat.types';

export interface WizardFormData {
  // Step 1: Boat & Photos (REQUIRED: manufacturer, model, model_year, condition)
  condition: 'new' | 'used';
  manufacturer: string;
  model: string;
  model_year: number;
  featured_media_id: number | null;
  gallery_media_ids: number[];

  // Step 2: Specs & Config (all optional)
  length_ft: number | null;
  beam_in: number | null;
  draft_in: number | null;
  seating_capacity: number | null;
  exterior_color: string;
  interior_color: string;
  hin_number: string;
  fuel_type: string;
  boat_types: string[];
  engine_type: string;
  engines: InventoryEngine[];
  trailer_vin: string;
  trailer_make: string;
  trailer_model: string;
  trailer_axles: string;
  // #515: read-only, non-submitted — the attached trailer asset's cost, seeded
  // from boat.trailers[0].cost_price. Feeds the invoice auto-calc only.
  linked_trailer_cost_price: number | null;
  standard_equipment: InventoryEquipment[];
  optional_equipment: InventoryEquipment[];

  // Step 3: Pricing
  our_price: number | null;
  msrp: number | null;
  regular_price: number | null;
  sale_price: number | null;
  cost_price: number | null;
  is_on_sale: boolean;
  boat_cost_price: number | null;
  boat_retail_price: number | null;
  invoice_amount: number | null;
  rigging: number | null;
  freight: number | null;

  // Step 4: Listing Details (REQUIRED: location_ids, primary_location_id)
  description: string;
  short_description: string;
  // Phase 50: collapsed enum + renamed FK column.
  website_status: 'published' | 'disabled';
  status_id: string;
  first_available_date: string | null;
  ribbon_id: string;
  ribbon_expiry_type: 'never' | 'days' | 'date';
  ribbon_expiry_value: string;
  location_ids: string[];
  primary_location_id: string;

  // Step 5: Review & Create (no editable fields)
}

export interface WizardStepConfig {
  id: number;
  label: string;
  description: string;
  icon: LucideIcon;
}

export const WIZARD_STEPS: WizardStepConfig[] = [
  { id: 0, label: 'Boat & Photos', description: 'Identity, condition & images', icon: Ship },
  { id: 1, label: 'Specs & Config', description: 'Dimensions, engines & trailer', icon: SlidersHorizontal },
  { id: 2, label: 'Pricing', description: 'Prices & cost breakdown', icon: DollarSign },
  { id: 3, label: 'Listing Details', description: 'Description, status & location', icon: FileText },
  { id: 4, label: 'Review & Create', description: 'Verify and submit', icon: ClipboardCheck },
];

export const WIZARD_DEFAULT_VALUES: WizardFormData = {
  condition: 'new',
  manufacturer: '',
  model: '',
  model_year: new Date().getFullYear(),
  length_ft: null,
  beam_in: null,
  draft_in: null,
  seating_capacity: null,
  exterior_color: '',
  interior_color: '',
  hin_number: '',
  fuel_type: '',
  boat_types: [],
  standard_equipment: [],
  optional_equipment: [],
  engine_type: '',
  engines: [],
  trailer_vin: '',
  trailer_make: '',
  trailer_model: '',
  trailer_axles: '',
  linked_trailer_cost_price: null,
  description: '',
  short_description: '',
  website_status: 'disabled',
  status_id: '',
  first_available_date: null,
  ribbon_id: '',
  ribbon_expiry_type: 'never',
  ribbon_expiry_value: '',
  our_price: null,
  msrp: null,
  regular_price: null,
  sale_price: null,
  cost_price: null,
  is_on_sale: false,
  boat_cost_price: null,
  boat_retail_price: null,
  invoice_amount: null,
  rigging: null,
  freight: null,
  featured_media_id: null,
  gallery_media_ids: [],
  location_ids: [],
  primary_location_id: '',
};
